import React from 'react';
import { Mail, MessageSquare, XCircle, AlertCircle, CheckCircle, Sparkles, Plus, Trash2, Pencil, Loader2, Palette } from 'lucide-react';
import { renderTemplate, purifyTemplate } from '../utils/templateUtils';

const TemplatesPage = ({
  templates,
  activeTab,
  setActiveTab,
  newTemplate,
  setNewTemplate,
  saveTemplate,
  templateSaving,
  templateDeletingId,
  selectedTemplate,
  setSelectedTemplate,
  deleteTemplate,
  loadTemplateForEdit,
  resetNewTemplate,
  settings
}) => {
  const previewSampleContact = React.useMemo(
    () => ({ firstName: 'John', lastName: 'Doe', email: 'john@example.com' }),
    []
  );
  const [extras, setExtras] = React.useState({ buttonLabel: '', buttonUrl: '', imageUrl: '' });
  const extraMarker = '<!-- mr-extra -->';
  const escapeAttr = (value) => String(value || '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
  const escapeText = (value) => String(value || '').replace(/&/g, '&amp;').replace(/</g, '&lt;');
  const letterFromBody = (body) => String(body || '').split(extraMarker)[0].replace(/\s+$/, '');
  const bodyWithExtras = (letter, nextExtras) => {
    const bits = [];
    if (nextExtras.imageUrl) {
      bits.push(`<p><img src="${escapeAttr(nextExtras.imageUrl)}" alt="" style="max-width:100%;height:auto;" /></p>`);
    }
    if (nextExtras.buttonLabel && nextExtras.buttonUrl) {
      bits.push(`<p><a href="${escapeAttr(nextExtras.buttonUrl)}" style="display:inline-block;padding:12px 18px;background:#111111;color:#ffffff;text-decoration:none;border-radius:8px;">${escapeText(nextExtras.buttonLabel)}</a></p>`);
    }
    if (!bits.length) return letter;
    return `${letter}\n\n${extraMarker}\n<div>${bits.join('')}</div>`;
  };
  const updateLetter = (letter) => setNewTemplate({ ...newTemplate, body: bodyWithExtras(letter, extras) });
  const updateExtras = (nextExtras) => {
    setExtras(nextExtras);
    setNewTemplate({ ...newTemplate, body: bodyWithExtras(letterFromBody(newTemplate.body), nextExtras) });
  };

  const editorPreviewTemplate = React.useMemo(() => {
    const payload = {
      body: newTemplate.body || '',
      previewText: newTemplate.previewText || ''
    };
    if (newTemplate.type === 'email' && newTemplate.subject) {
      payload.subject = newTemplate.subject;
    }
    return renderTemplate(payload, previewSampleContact, settings);
  }, [newTemplate.body, newTemplate.subject, newTemplate.previewText, newTemplate.type, newTemplate.fromName, newTemplate.fromEmail, newTemplate.replyTo, newTemplate.emailStyle, newTemplate.accentColor, newTemplate.brandLabel, newTemplate.includeSignature, newTemplate.signature, newTemplate.customCss, previewSampleContact, settings]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Templates</h1>
          <p className="text-gray-600 mt-1">Design email and SMS templates, set who they are from, and save your own style</p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={() => setActiveTab('list')}
            className={`px-4 py-2 rounded-lg font-semibold transition-all duration-300 ${
              activeTab === 'list' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700 border border-gray-300'
            }`}
          >
            Library
          </button>
          <button 
            onClick={() => {
              resetNewTemplate();
              setActiveTab('create');
            }}
            className={`px-4 py-2 rounded-lg font-semibold transition-all duration-300 ${
              activeTab === 'create' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700 border border-gray-300'
            }`}
          >
            Create New
          </button>
        </div>
      </div>

      {/* Template Library */}
      {activeTab === 'list' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map(template => {
            const { issues, warnings, score } = purifyTemplate(template, settings);
            return (
              <div
                key={template.id}
                className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 cursor-pointer group"
                onClick={() => setSelectedTemplate(template)}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={`p-3 ${template.type === 'email' ? 'bg-indigo-100' : 'bg-purple-100'} rounded-xl group-hover:scale-110 transition-transform duration-300`}>
                    {template.type === 'email' 
                      ? <Mail className="text-indigo-600" size={24} /> 
                      : <MessageSquare className="text-purple-600" size={24} />
                    }
                  </div>
                  <div className={`px-3 py-1 rounded-full text-xs font-bold ${
                    score >= 90 ? 'bg-green-100 text-green-800' :
                    score >= 70 ? 'bg-blue-100 text-blue-800' :
                    score >= 50 ? 'bg-amber-100 text-amber-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {score}/100
                  </div>
                </div>
                
                <div className="mb-3">
                  <div className="font-bold text-gray-900 text-lg mb-1">{template.name}</div>
                  <div className="text-xs text-gray-600 font-semibold">{template.category} • {template.type.toUpperCase()}</div>
                </div>

                {template.subject && (
                  <div className="text-sm text-gray-700 font-medium mb-2 line-clamp-1">
                    📧 {template.subject}
                  </div>
                )}

                {template.type === 'email' && (template.fromName || template.fromEmail) && (
                  <div className="text-xs text-gray-500 mb-2 line-clamp-1">
                    From: {template.fromName || 'No name'}{template.fromEmail ? ` <${template.fromEmail}>` : ''}
                  </div>
                )}
                
                {template.previewText && (
                  <div className="text-xs text-gray-500 mb-2 italic line-clamp-1">
                    Preview: {template.previewText}
                  </div>
                )}
                
                <div className="text-sm text-gray-600 line-clamp-3 mb-4">{template.body}</div>

                {(issues.length > 0 || warnings.length > 0) && (
                  <div className="space-y-1">
                    {issues.slice(0, 2).map((issue, i) => (
                      <div key={i} className="flex items-center gap-2 text-red-700 text-xs">
                        <XCircle size={12} />
                        <span className="line-clamp-1">{issue}</span>
                      </div>
                    ))}
                    {warnings.slice(0, 1).map((warning, i) => (
                      <div key={i} className="flex items-center gap-2 text-amber-700 text-xs">
                        <AlertCircle size={12} />
                        <span className="line-clamp-1">{warning}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        /* Template Editor */
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Editor Form */}
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Template Editor</h2>
            {newTemplate.id && (
              <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                Editing existing template. Changes will update the original once saved.
              </div>
            )}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Template Name *</label>
                <input
                  type="text"
                  value={newTemplate.name}
                  onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300"
                  placeholder="e.g., Summer Sale Announcement"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Type *</label>
                  <select
                    value={newTemplate.type}
                    onChange={(e) => setNewTemplate({ ...newTemplate, type: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300"
                  >
                    <option value="email">Email</option>
                    <option value="sms">SMS</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Category</label>
                  <select
                    value={newTemplate.category}
                    onChange={(e) => setNewTemplate({ ...newTemplate, category: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300"
                  >
                    <option value="Welcome">Welcome</option>
                    <option value="Reply">Reply</option>
                    <option value="Info">Info</option>
                    <option value="Promotional">Promotional</option>
                    <option value="Transactional">Transactional</option>
                    <option value="Custom">Custom</option>
                  </select>
                </div>
              </div>

              {newTemplate.type === 'email' && (
                <>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Subject Line *</label>
                  <input
                    type="text"
                    value={newTemplate.subject}
                    onChange={(e) => setNewTemplate({ ...newTemplate, subject: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300"
                    placeholder="Your compelling subject line"
                  />
                  <div className="text-xs text-gray-500 mt-1">{newTemplate.subject.length}/60 characters</div>
                </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 mt-4">Preview Text</label>
                    <input
                      type="text"
                      value={newTemplate.previewText}
                      onChange={(e) => setNewTemplate({ ...newTemplate, previewText: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300"
                      placeholder="Appears next to the subject line in inboxes"
                    />
                    <div className="text-xs text-gray-500 mt-1">{newTemplate.previewText.length}/140 characters</div>
                  </div>

                  <div className="rounded-xl border border-gray-200 p-4 space-y-4">
                    <div>
                      <div className="text-sm font-semibold text-gray-900">Sender</div>
                      <p className="text-xs text-gray-500 mt-1">This is who the email is from. It is saved with the template and is not signed by a default company.</p>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">From name</label>
                      <input
                        type="text"
                        value={newTemplate.fromName || ''}
                        onChange={(e) => setNewTemplate({ ...newTemplate, fromName: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300"
                        placeholder="e.g., Alex at Acme"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">From email</label>
                      <input
                        type="email"
                        value={newTemplate.fromEmail || ''}
                        onChange={(e) => setNewTemplate({ ...newTemplate, fromEmail: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300"
                        placeholder={settings?.from_email || 'hello@yourdomain.com'}
                      />
                      <div className="text-xs text-gray-500 mt-1">Must be a verified sending address in SendGrid.</div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Reply-to (optional)</label>
                      <input
                        type="email"
                        value={newTemplate.replyTo || ''}
                        onChange={(e) => setNewTemplate({ ...newTemplate, replyTo: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300"
                        placeholder="Replies go here if different from the from email"
                      />
                    </div>
                  </div>

                  <div className="rounded-xl border border-gray-200 p-4 space-y-4">
                    <div className="flex items-center gap-2">
                      <Palette size={16} className="text-indigo-600" />
                      <div>
                        <div className="text-sm font-semibold text-gray-900">Email design</div>
                        <p className="text-xs text-gray-500">Choose a layout, then save it with this template.</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {[
                        { id: 'plain', title: 'Personal note', desc: 'Looks like a normal email' },
                        { id: 'branded', title: 'Simple branded note', desc: 'A light header and one color' },
                        { id: 'custom', title: 'Advanced', desc: 'Raw HTML, only if you need it' }
                      ].map((style) => (
                        <button
                          key={style.id}
                          type="button"
                          onClick={() => setNewTemplate({ ...newTemplate, emailStyle: style.id })}
                          className={`text-left rounded-xl border p-3 transition-all duration-200 ${
                            newTemplate.emailStyle === style.id
                              ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-200'
                              : 'border-gray-200 bg-white hover:border-gray-300'
                          }`}
                        >
                          <div className="text-sm font-semibold text-gray-900">{style.title}</div>
                          <div className="text-xs text-gray-500 mt-1">{style.desc}</div>
                        </button>
                      ))}
                    </div>

                    {newTemplate.emailStyle === 'branded' && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Brand label</label>
                          <input
                            type="text"
                        value={newTemplate.brandLabel || ''}
                            onChange={(e) => setNewTemplate({ ...newTemplate, brandLabel: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300"
                            placeholder="Shown at the top of the email"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Accent color</label>
                          <div className="flex items-center gap-3">
                            <input
                              type="color"
                              value={newTemplate.accentColor || '#4f46e5'}
                              onChange={(e) => setNewTemplate({ ...newTemplate, accentColor: e.target.value })}
                              className="h-10 w-12 rounded border border-gray-300 bg-white p-1"
                            />
                            <input
                              type="text"
                              value={newTemplate.accentColor || '#4f46e5'}
                              onChange={(e) => setNewTemplate({ ...newTemplate, accentColor: e.target.value })}
                              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300 font-mono text-sm"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {newTemplate.emailStyle === 'custom' && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Custom CSS (optional)</label>
                      <textarea
                        value={newTemplate.customCss || ''}
                        onChange={(e) => setNewTemplate({ ...newTemplate, customCss: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300 h-24 font-mono text-xs"
                        placeholder=".cta { background: #111; color: #fff; }"
                      ></textarea>
                    </div>
                    )}

                    <label className="flex items-start gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={Boolean(newTemplate.includeSignature)}
                        onChange={(e) => setNewTemplate({ ...newTemplate, includeSignature: e.target.checked })}
                        className="mt-1 rounded"
                      />
                      <span>
                        Add my own signature
                        <span className="block text-xs text-gray-500">Off by default. Nothing is added unless you write it here.</span>
                      </span>
                    </label>
                    {newTemplate.includeSignature && (
                      <textarea
                        value={newTemplate.signature || ''}
                        onChange={(e) => setNewTemplate({ ...newTemplate, signature: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300 h-24 font-mono text-sm"
                        placeholder={"Alex\nFounder, Acme"}
                      ></textarea>
                    )}
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {newTemplate.type === 'email' && newTemplate.emailStyle !== 'custom' ? 'Letter' : 'Message Body *'}
                </label>
                <textarea
                  value={newTemplate.type === 'email' && newTemplate.emailStyle !== 'custom' ? letterFromBody(newTemplate.body) : newTemplate.body}
                  onChange={(e) => (
                    newTemplate.type === 'email' && newTemplate.emailStyle !== 'custom'
                      ? updateLetter(e.target.value)
                      : setNewTemplate({ ...newTemplate, body: e.target.value })
                  )}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300 h-48 text-sm"
                  placeholder={newTemplate.type === 'email' ? 'Write the letter the way you would write it to one person.' : 'Write your message here...'}
                ></textarea>
                {newTemplate.type === 'email' && newTemplate.emailStyle !== 'custom' && (
                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    <input
                      type="text"
                      value={extras.buttonLabel}
                      onChange={(e) => updateExtras({ ...extras, buttonLabel: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="Button label (optional)"
                    />
                    <input
                      type="url"
                      value={extras.buttonUrl}
                      onChange={(e) => updateExtras({ ...extras, buttonUrl: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="Button link (optional)"
                    />
                    <input
                      type="url"
                      value={extras.imageUrl}
                      onChange={(e) => updateExtras({ ...extras, imageUrl: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg sm:col-span-2"
                      placeholder="Image link (optional)"
                    />
                  </div>
                )}
                <div className="text-xs text-gray-500 mt-1">
                  {newTemplate.body.length} characters
                  {newTemplate.type === 'sms' && ` • ${Math.ceil(newTemplate.body.length / 160)} SMS segment(s)`}
                </div>
              </div>

              <div className="p-4 bg-indigo-50 rounded-lg">
                <div className="font-semibold text-indigo-900 mb-2 text-sm">Available Tokens:</div>
                <div className="flex flex-wrap gap-2">
                  {['{{first_name}}', '{{last_name}}', '{{email}}', '{{company_name}}', '{{unsubscribe_link}}'].map(token => (
                    <button
                      key={token}
                      onClick={() => setNewTemplate({ ...newTemplate, body: newTemplate.body + token })}
                      className="px-3 py-1 bg-white border border-indigo-200 rounded-lg text-xs font-mono text-indigo-700 hover:bg-indigo-100 transition-colors duration-200"
                    >
                      {token}
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={saveTemplate}
                disabled={templateSaving || !newTemplate.name || !newTemplate.body || (newTemplate.type === 'email' && !newTemplate.subject)}
                className="w-full px-4 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {templateSaving ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : newTemplate.id ? (
                  <Pencil size={18} />
                ) : (
                  <Plus size={18} />
                )}
                {templateSaving ? 'Saving...' : newTemplate.id ? 'Update Template' : 'Save Template'}
              </button>
              {newTemplate.id && (
                <button
                  onClick={() => {
                    if (window.confirm('Delete this template? This action cannot be undone.')) {
                      deleteTemplate(newTemplate.id);
                    }
                  }}
                  disabled={templateDeletingId === newTemplate.id}
                  className="w-full px-4 py-3 border border-red-300 text-red-600 rounded-xl font-semibold shadow-lg hover:shadow-xl hover:bg-red-50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {templateDeletingId === newTemplate.id ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <Trash2 size={18} />
                  )}
                  {templateDeletingId === newTemplate.id ? 'Deleting...' : 'Delete Template'}
                </button>
              )}
            </div>
          </div>

          {/* Purification & Preview */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Purification Check</h2>
              {newTemplate.body ? (
                <div>
                  {(() => {
                    const { issues, warnings, score } = purifyTemplate(newTemplate, settings);
                    return (
                      <>
                        <div className="mb-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-semibold text-gray-700">Deliverability Score</span>
                            <span className={`text-2xl font-bold ${
                              score >= 90 ? 'text-green-600' :
                              score >= 70 ? 'text-blue-600' :
                              score >= 50 ? 'text-amber-600' :
                              'text-red-600'
                            }`}>{score}/100</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-3">
                            <div 
                              className={`h-3 rounded-full transition-all duration-500 ${
                                score >= 90 ? 'bg-gradient-to-r from-green-500 to-emerald-500' :
                                score >= 70 ? 'bg-gradient-to-r from-blue-500 to-cyan-500' :
                                score >= 50 ? 'bg-gradient-to-r from-amber-500 to-orange-500' :
                                'bg-gradient-to-r from-red-500 to-pink-500'
                              }`}
                              style={{ width: `${score}%` }}
                            ></div>
                          </div>
                        </div>

                        {issues.length === 0 && warnings.length === 0 ? (
                          <div className="flex items-center gap-2 text-green-700 bg-green-50 p-4 rounded-lg">
                            <CheckCircle size={20} />
                            <span className="text-sm font-semibold">Perfect! No issues detected.</span>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {issues.map((issue, i) => (
                              <div key={i} className="flex items-start gap-2 text-red-700 bg-red-50 p-3 rounded-lg">
                                <XCircle size={18} className="flex-shrink-0 mt-0.5" />
                                <span className="text-sm">{issue}</span>
                              </div>
                            ))}
                            {warnings.map((warning, i) => (
                              <div key={i} className="flex items-start gap-2 text-amber-700 bg-amber-50 p-3 rounded-lg">
                                <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
                                <span className="text-sm">{warning}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Sparkles className="mx-auto mb-2 text-gray-400" size={32} />
                  <p className="text-sm">Start writing to see purification suggestions</p>
                </div>
              )}
            </div>

            {newTemplate.body && (
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Preview</h2>
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  {newTemplate.type === 'email' && (
                    <div className="mb-3 pb-3 border-b border-gray-300 space-y-1">
                      <div className="text-xs text-gray-600">
                        <span className="font-semibold text-gray-700">From:</span>{' '}
                        {editorPreviewTemplate.fromName || 'No name'}
                        {editorPreviewTemplate.fromEmail ? ` <${editorPreviewTemplate.fromEmail}>` : ''}
                      </div>
                      {newTemplate.subject && (
                        <div>
                          <div className="text-xs text-gray-600 mb-1">Subject:</div>
                          <div className="font-semibold text-gray-900">
                            {editorPreviewTemplate.subject || newTemplate.subject}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {newTemplate.type === 'email' && (
                    <div className="mb-3 text-xs text-gray-600">
                      <span className="font-semibold text-gray-700">Preview text:</span>{' '}
                      <span className="text-gray-800">{editorPreviewTemplate.previewText || '—'}</span>
                  </div>
                  )}
                  {editorPreviewTemplate.html ? (
                    <div
                      className="text-sm text-gray-900 [word-break:break-word]"
                      dangerouslySetInnerHTML={{ __html: editorPreviewTemplate.html }}
                    />
                  ) : (
                    <div className="text-sm text-gray-500 italic">Start writing to see your preview.</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Template Detail Modal */}
      {selectedTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 animate-[fadeIn_0.2s_ease-out]" onClick={() => setSelectedTemplate(null)}>
          <div className="bg-white rounded-2xl p-8 max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Template Details</h2>
              <button onClick={() => setSelectedTemplate(null)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200">
                <XCircle size={24} className="text-gray-600" />
              </button>
            </div>
            
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className={`p-4 ${selectedTemplate.type === 'email' ? 'bg-indigo-100' : 'bg-purple-100'} rounded-xl`}>
                  {selectedTemplate.type === 'email' 
                    ? <Mail className="text-indigo-600" size={32} /> 
                    : <MessageSquare className="text-purple-600" size={32} />
                  }
                </div>
                <div className="flex-1">
                  <div className="text-2xl font-bold text-gray-900">{selectedTemplate.name}</div>
                  <div className="text-gray-600">{selectedTemplate.category} • {selectedTemplate.type.toUpperCase()}</div>
                </div>
              </div>
              
              {selectedTemplate.subject && (
                <div>
                  <div className="text-sm font-semibold text-gray-700 mb-2">Subject Line</div>
                  <div className="p-3 bg-gray-50 rounded-lg text-gray-900 font-medium">{selectedTemplate.subject}</div>
                </div>
              )}

              {selectedTemplate.type === 'email' && (
                <div>
                  <div className="text-sm font-semibold text-gray-700 mb-2">From</div>
                  <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                    {selectedTemplate.fromName || 'No name'}
                    {selectedTemplate.fromEmail ? ` <${selectedTemplate.fromEmail}>` : ''}
                    {selectedTemplate.replyTo ? ` · replies to ${selectedTemplate.replyTo}` : ''}
                  </div>
                </div>
              )}

              {selectedTemplate.type === 'email' && (
                <div>
                  <div className="text-sm font-semibold text-gray-700 mb-2">Design</div>
                  <div className="p-3 bg-gray-50 rounded-lg text-gray-900 capitalize">
                    {selectedTemplate.emailStyle || 'plain'}
                    {selectedTemplate.brandLabel ? ` · ${selectedTemplate.brandLabel}` : ''}
                  </div>
                </div>
              )}

              {selectedTemplate.type === 'email' && (
                <div>
                  <div className="text-sm font-semibold text-gray-700 mb-2">Preview Text</div>
                  <div className="p-3 bg-gray-50 rounded-lg text-gray-700">{selectedTemplate.previewText || 'Not set'}</div>
                </div>
              )}
              
              <div>
                <div className="text-sm font-semibold text-gray-700 mb-2">Message Body</div>
                <div className="p-4 bg-gray-50 rounded-lg text-gray-900 whitespace-pre-wrap font-mono text-sm">{selectedTemplate.body}</div>
              </div>

              <div>
                <div className="text-sm font-semibold text-gray-700 mb-3">Purification Analysis</div>
                {(() => {
                  const { issues, warnings, score } = purifyTemplate(selectedTemplate, settings);
                  return (
                    <>
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-gray-600">Deliverability Score</span>
                          <span className={`text-xl font-bold ${
                            score >= 90 ? 'text-green-600' :
                            score >= 70 ? 'text-blue-600' :
                            score >= 50 ? 'text-amber-600' :
                            'text-red-600'
                          }`}>{score}/100</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full transition-all duration-500 ${
                              score >= 90 ? 'bg-gradient-to-r from-green-500 to-emerald-500' :
                              score >= 70 ? 'bg-gradient-to-r from-blue-500 to-cyan-500' :
                              score >= 50 ? 'bg-gradient-to-r from-amber-500 to-orange-500' :
                              'bg-gradient-to-r from-red-500 to-pink-500'
                            }`}
                            style={{ width: `${score}%` }}
                          ></div>
                        </div>
                      </div>

                      {issues.length === 0 && warnings.length === 0 ? (
                        <div className="flex items-center gap-2 text-green-700 bg-green-50 p-4 rounded-lg">
                          <CheckCircle size={20} />
                          <span className="font-semibold">Template looks great! Ready to use.</span>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {issues.map((issue, i) => (
                            <div key={i} className="flex items-start gap-2 text-red-700 bg-red-50 p-3 rounded-lg">
                              <XCircle size={18} className="flex-shrink-0 mt-0.5" />
                              <span className="text-sm">{issue}</span>
                            </div>
                          ))}
                          {warnings.map((warning, i) => (
                            <div key={i} className="flex items-start gap-2 text-amber-700 bg-amber-50 p-3 rounded-lg">
                              <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
                              <span className="text-sm">{warning}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>

              <div>
                <div className="text-sm font-semibold text-gray-700 mb-3">Sample Preview</div>
                <div className="p-4 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg border border-indigo-200">
                  {selectedTemplate.subject && (
                    <div className="mb-3 pb-3 border-b border-indigo-200">
                      <div className="text-xs text-indigo-700 mb-1">
                        From: {renderTemplate(selectedTemplate, { firstName: 'Sarah', lastName: 'Johnson', email: 'sarah@example.com' }, settings).fromName || selectedTemplate.fromName || 'No name'}
                        {(selectedTemplate.fromEmail) ? ` <${selectedTemplate.fromEmail}>` : ''}
                      </div>
                      <div className="text-xs text-indigo-700 mb-1">Subject:</div>
                      <div className="font-semibold text-gray-900">
                        {renderTemplate(selectedTemplate, { firstName: 'Sarah', lastName: 'Johnson', email: 'sarah@example.com' }, settings).subject}
                      </div>
                    </div>
                  )}
                  {(() => {
                    const renderedSample = renderTemplate(
                      selectedTemplate,
                      { firstName: 'Sarah', lastName: 'Johnson', email: 'sarah@example.com' },
                      settings
                    );
                    return (
                      <>
                        {selectedTemplate.type === 'email' && (
                          <div className="mb-3 text-xs text-indigo-700">
                            <span className="font-semibold uppercase tracking-wide mr-1">Preview:</span>
                            <span className="text-gray-900">{renderedSample.previewText || 'Not set'}</span>
                  </div>
                        )}
                        <div
                          className="text-sm text-gray-900 [word-break:break-word]"
                          dangerouslySetInnerHTML={{ __html: renderedSample.html || renderedSample.body || '' }}
                        />
                      </>
                    );
                  })()}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    loadTemplateForEdit(selectedTemplate);
                    setSelectedTemplate(null);
                  }}
                  className="px-4 py-2 rounded-lg border border-indigo-200 text-indigo-700 hover:bg-indigo-50 transition-colors duration-200 flex items-center gap-2"
                >
                  <Pencil size={16} />
                  Edit Template
                </button>
                <button
                  onClick={() => deleteTemplate(selectedTemplate.id)}
                  disabled={templateDeletingId === selectedTemplate.id}
                  className="px-4 py-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-colors duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {templateDeletingId === selectedTemplate.id ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                  {templateDeletingId === selectedTemplate.id ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplatesPage;
